import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Loader,
  KanbanSquare,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { kanbanApiService } from '../../services/kanbanApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  kanbanTeamsFromPermissions,
  kanbanTeamsFromStaffTeams,
  mergeKanbanTeamLists,
} from '../../utils/kanbanPermissions';
import { teamStaffOnlyKanbanWrite } from '../../utils/tesseractTeamAccess';
import { kanbanTeamColor } from '../../utils/kanbanTeamColors';
import { cn } from '../../utils/cn';
import type { KanbanBoard, KanbanList } from '../../types/kanban';
import type {
  IncidentKanbanLink,
  PromoteIncidentToCardRequest,
  PromoteIncidentCardType,
} from '../../types/incident';

const CARD_TYPES: PromoteIncidentCardType[] = [
  'task',
  'bug',
  'feature',
  'story',
  'improvement',
  'epic',
  'subtask',
  'other',
];

/**
 * Default sync flags applied to every new bridge link. Per UX feedback we no longer
 * surface these as five separate checkboxes — the bridge is what makes the two sides
 * "the same thing", so all sync flags default to ON. Users who need finer-grained
 * control can toggle individual flags later from the Workspaces panel on the incident
 * detail page (`<IncidentBridgeRow>`).
 */
const ALL_SYNC_ON: Required<
  Pick<
    PromoteIncidentToCardRequest,
    'sync_status' | 'sync_priority' | 'sync_assignees' | 'sync_comments' | 'propagate_watchers'
  >
> = {
  sync_status: true,
  sync_priority: true,
  sync_assignees: true,
  sync_comments: true,
  propagate_watchers: true,
};

export interface PromoteToWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  incidentId: string;
  /** Called after a successful promote (or idempotent open of an existing card). */
  onSuccess: (link: IncidentKanbanLink, cardId: string, createdCard: boolean) => void;
}

export const PromoteToWorkspaceModal = ({
  isOpen,
  onClose,
  incidentId,
  onSuccess,
}: PromoteToWorkspaceModalProps) => {
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  // ── Permission-aware team list ─────────────────────────────────────────
  const writableTeams = useMemo(() => {
    if (!identity && !isDev) return [];
    const permissions = Array.isArray(identity?.raw?.custom?.permissions)
      ? (identity!.raw.custom.permissions as string[])
      : [];
    const groups = Array.isArray(identity?.raw?.custom?.groups)
      ? (identity!.raw.custom.groups as string[])
      : [];
    const isSuper =
      isDev || groups.includes('SPY_Super_admin') || permissions.includes('argus.kanban.super');
    const fromPermissions = kanbanTeamsFromPermissions(permissions);
    const fromStaff = kanbanTeamsFromStaffTeams(identity?.raw?.custom?.staff_teams);
    const all = mergeKanbanTeamLists(fromPermissions, fromStaff);
    if (isSuper) return all.length > 0 ? all : ['devteam', 'buildteam', 'eventteam', 'management'];
    return all.filter(
      (t) =>
        permissions.includes(`argus.kanban.write.${t}`) ||
        permissions.includes(`argus.kanban.manage.${t}`) ||
        teamStaffOnlyKanbanWrite(identity, isDev, t),
    );
  }, [identity, isDev]);

  // ── Form state ─────────────────────────────────────────────────────────
  const [team, setTeam] = useState<string>('');
  const [useDefaultBoard, setUseDefaultBoard] = useState(true);
  const [boardId, setBoardId] = useState<string>('');
  const [listId, setListId] = useState<string>('');
  const [cardType, setCardType] = useState<PromoteIncidentCardType>('task');
  const [copyAssignees, setCopyAssignees] = useState(true);
  const [notes, setNotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Sync flags default ON for new bridges and are not surfaced here — see `ALL_SYNC_ON`.

  // ── Async state ─────────────────────────────────────────────────────────
  const [boards, setBoards] = useState<KanbanBoard[] | null>(null);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [lists, setLists] = useState<KanbanList[] | null>(null);
  const [listsLoading, setListsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    link: IncidentKanbanLink;
    cardId: string;
    boardId: string;
    createdCard: boolean;
  } | null>(null);

  // Initialise team to the first writable one.
  useEffect(() => {
    if (!isOpen) return;
    if (writableTeams.length > 0 && !team) {
      setTeam(writableTeams[0]!);
    }
  }, [isOpen, writableTeams, team]);

  // Load boards when team changes.
  useEffect(() => {
    if (!isOpen || !team || useDefaultBoard) {
      setBoards(null);
      return;
    }
    let cancelled = false;
    setBoardsLoading(true);
    kanbanApiService
      .listBoards({ team, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setBoards(Array.isArray(res.results) ? res.results : []);
      })
      .catch(() => {
        if (!cancelled) setBoards([]);
      })
      .finally(() => {
        if (!cancelled) setBoardsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, team, useDefaultBoard]);

  // Load lists when a specific board is chosen.
  useEffect(() => {
    if (!isOpen || !boardId || useDefaultBoard) {
      setLists(null);
      return;
    }
    let cancelled = false;
    setListsLoading(true);
    kanbanApiService
      .listLists(boardId)
      .then((res) => {
        if (!cancelled) setLists(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (!cancelled) setLists([]);
      })
      .finally(() => {
        if (!cancelled) setListsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, boardId, useDefaultBoard]);

  // Reset form on close.
  useEffect(() => {
    if (isOpen) return;
    setSuccess(null);
    setError(null);
    setSubmitting(false);
    setNotes('');
    setShowAdvanced(false);
    setUseDefaultBoard(true);
    setBoardId('');
    setListId('');
    setCardType('task');
    setCopyAssignees(true);
  }, [isOpen]);

  if (!isOpen) return null;

  const noTeams = writableTeams.length === 0;

  const handleSubmit = async () => {
    if (!team) return;
    setSubmitting(true);
    setError(null);
    const body: PromoteIncidentToCardRequest = {
      team,
      ...(boardId && !useDefaultBoard ? { board_id: boardId } : {}),
      ...(listId && !useDefaultBoard ? { list_id: listId } : {}),
      card_type: cardType,
      copy_assignees: copyAssignees,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...ALL_SYNC_ON,
    };
    try {
      const resp = await incidentsApiService.promoteToCard(incidentId, body);
      setSuccess({
        link: resp.link,
        cardId: resp.card.id,
        boardId: resp.link.board_id,
        createdCard: resp.created_card,
      });
      onSuccess(resp.link, resp.card.id, resp.created_card);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to promote to workspace';
      // Specialise the 403 message per the integration guide UX recipe.
      if (msg.includes('Missing argus.kanban.write')) {
        setError(`${msg}. Ask an admin to grant you that permission.`);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenInWorkspace = () => {
    if (!success) return;
    navigate(
      `/workspaces/${encodeURIComponent(success.boardId)}?card=${encodeURIComponent(success.cardId)}`,
    );
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-[9999]"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 className="modal-title flex items-center gap-2">
              <KanbanSquare className="w-5 h-5 text-accent-400" />
              Promote to workspace
            </h2>
            <button onClick={onClose} className="modal-close" disabled={submitting} title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {success ? (
            <div className="space-y-4">
              <div className="alert alert-success" role="status">
                <CheckCircle />
                <div>
                  {success.createdCard ? (
                    <>
                      Card created and bridged to this incident on team{' '}
                      <span className="font-mono">{team}</span>.
                    </>
                  ) : (
                    <>
                      An existing bridged card was reused on team{' '}
                      <span className="font-mono">{team}</span> — no duplicate created.
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={onClose} className="glass-button-secondary">
                  Close
                </button>
                <button onClick={handleOpenInWorkspace} className="btn-primary">
                  <ExternalLink className="w-4 h-4" />
                  Open in workspace
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-glass-400 mb-4">
                Creates a new kanban card on the chosen team and bridges it to this incident. If a
                bridge already exists for the chosen <em>(incident, board)</em> pair, the existing
                card will be reused instead of creating a duplicate.
              </p>

              {/* Team picker */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Team
                </label>
                {noTeams ? (
                  <div className="alert alert-warning" role="alert">
                    <AlertCircle />
                    <span>
                      You don&apos;t have kanban write access on any team. Ask an admin for{' '}
                      <span className="font-mono">argus.kanban.write.&lt;team&gt;</span>.
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {writableTeams.map((t) => {
                      const color = kanbanTeamColor(t);
                      const active = t === team;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => {
                            setTeam(t);
                            setBoardId('');
                            setListId('');
                          }}
                          aria-pressed={active}
                          className={cn(
                            'inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                            active
                              ? 'bg-accent-500/15 border-accent-500/40 text-white'
                              : 'bg-glass-100/5 border-glass-200/10 text-glass-300 hover:text-white hover:border-glass-200/20',
                          )}
                          style={
                            active
                              ? { boxShadow: `inset 0 0 0 1px ${color}40` }
                              : undefined
                          }
                        >
                          <span
                            className="inline-block w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: color }}
                          />
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sync is on by default for every flag — see ALL_SYNC_ON. */}
              <div className="alert alert-info mb-4 text-xs">
                <span>
                  Status, priority, assignees, comments, and watchers will all sync between this
                  incident and the new card. You can disable individual flags later from the
                  Workspaces panel on the incident page.
                </span>
              </div>

              {/* Copy assignees toggle */}
              <div className="mb-4">
                <label
                  className="flex items-center gap-2 text-sm text-glass-200 cursor-pointer"
                  title="Copy current incident_assignees onto the new card as kanban assignees."
                >
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={copyAssignees}
                    onChange={(e) => setCopyAssignees(e.target.checked)}
                  />
                  Copy current incident assignees onto the new card
                </label>
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional context recorded on the bridge link"
                  className="glass-input w-full"
                />
              </div>

              {/* Advanced: card type, board, list */}
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="btn-ghost btn-xs -ml-2 mb-3"
                aria-expanded={showAdvanced}
              >
                {showAdvanced ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                Advanced options
              </button>

              {showAdvanced && (
                <div className="space-y-3 mb-4 pl-3 border-l border-glass-200/10">
                  <div>
                    <label className="block text-xs font-medium text-glass-400 mb-1.5">
                      Card type
                    </label>
                    <select
                      value={cardType}
                      onChange={(e) => setCardType(e.target.value as PromoteIncidentCardType)}
                      className="glass-input w-full"
                    >
                      {CARD_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm text-glass-200 cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={useDefaultBoard}
                        onChange={(e) => {
                          setUseDefaultBoard(e.target.checked);
                          setBoardId('');
                          setListId('');
                        }}
                      />
                      Use the team&apos;s default board (auto-create if needed)
                    </label>
                  </div>

                  {!useDefaultBoard && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-glass-400 mb-1.5">
                          Board
                        </label>
                        {boardsLoading ? (
                          <div className="flex items-center gap-2 text-xs text-glass-400">
                            <Loader className="w-3.5 h-3.5 animate-spin" />
                            Loading boards…
                          </div>
                        ) : (
                          <select
                            value={boardId}
                            onChange={(e) => {
                              setBoardId(e.target.value);
                              setListId('');
                            }}
                            className="glass-input w-full"
                          >
                            <option value="">— select a board —</option>
                            {(boards ?? []).map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.title}
                                {b.is_default ? ' ★' : ''}
                                {b.is_archived ? ' (archived)' : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {boardId && (
                        <div>
                          <label className="block text-xs font-medium text-glass-400 mb-1.5">
                            List (optional — defaults to first list with role &quot;backlog&quot;)
                          </label>
                          {listsLoading ? (
                            <div className="flex items-center gap-2 text-xs text-glass-400">
                              <Loader className="w-3.5 h-3.5 animate-spin" />
                              Loading lists…
                            </div>
                          ) : (
                            <select
                              value={listId}
                              onChange={(e) => setListId(e.target.value)}
                              className="glass-input w-full"
                            >
                              <option value="">— default —</option>
                              {(lists ?? [])
                                .filter((l) => l.is_archived !== 1)
                                .sort((a, b) => a.position - b.position)
                                .map((l) => (
                                  <option key={l.id} value={l.id}>
                                    {l.title} ({l.list_role})
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {error && (
                <div className="alert alert-danger mb-4" role="alert">
                  <AlertCircle />
                  <span className="break-words">{error}</span>
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={onClose}
                  className="glass-button-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={!team || submitting || noTeams}
                  className="btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Promoting…
                    </>
                  ) : (
                    'Promote'
                  )}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};
